import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo from "@/hooks/useSeo";
import { siteInfo, testimonials } from "@/mocks/site";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "先核实，再发布",
    desc: "涉及具体人物的信息，至少需要两个独立来源交叉印证；无法确认的内容会明确标注「待回应」，不做主观定性。",
  },
  {
    icon: "ri-time-line",
    title: "保留时间线",
    desc: "娱乐事件常常持续发酵。我们为关键话题维护时间线，让后来接上的人也能快速看懂来龙去脉。",
  },
  {
    icon: "ri-scales-3-line",
    title: "呈现多方说法",
    desc: "在有争议的话题中，尽量同时呈现当事人、相关方与网友的主要观点，把判断留给你自己。",
  },
  {
    icon: "ri-user-unfollow-line",
    title: "拒绝人身攻击",
    desc: "批评可以，但不做与话题无关的人身羞辱；涉及隐私的信息一律不予呈现。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 18爆料网品牌介绍与内容原则",
    description:
      "18爆料网是聚焦娱乐热点与圈内话题的内容门户。了解我们的品牌定位、内容原则、编辑流程与读者口碑，以及线索投稿与联系方式。",
    keywords: "18爆料网,关于我们,内容原则,娱乐爆料,线索投稿",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 18爆料网",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        email: siteInfo.email,
        foundingDate: siteInfo.founded,
        description: siteInfo.description,
      },
    ],
  });

  return (
    <>
      <PageHeader
        title="关于 18爆料网"
        subtitle="一个把娱乐热点讲清楚的资讯门户。我们不追热度上限，只想让每一件事都有前因后果。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-primary-600">
              <span className="h-px w-6 bg-primary-500" />
              WHO WE ARE
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
              让碎片化的娱乐话题，有迹可循
            </h2>
            <p className="mt-5 text-sm leading-8 text-foreground-700">
              {siteInfo.name} 成立于 {siteInfo.founded} 年，起初只是几个爱看娱乐新闻的编辑在小圈子里交换信息。
              后来我们发现，很多人并不是不想了解，而是被碎片化的刷屏淹没了。
            </p>
            <p className="mt-4 text-sm leading-8 text-foreground-700">
              于是我们决定把热点做成结构：梳理时间线、标注来源、保留多方说法。
              你不必追着每一条动态跑，只要在我们这里花几分钟，就能把一件事看明白。
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-background-200 pt-6 sm:grid-cols-4">
              {siteInfo.stats.map((s) => (
                <div key={s.label}>
                  <dd className="font-heading text-2xl font-black text-primary-600">{s.value}</dd>
                  <dt className="mt-1 text-xs text-foreground-500">{s.label}</dt>
                </div>
              ))}
            </dl>
          </div>
          <div className="h-72 w-full overflow-hidden rounded-lg md:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20editorial%20desk%20scene%20with%20open%20notebooks%2C%20pressed%20flowers%20and%20a%20cup%20of%20coffee%2C%20soft%20morning%20light%20through%20linen%20curtains%2C%20muted%20amber%20and%20cream%20palette%2C%20calm%20magazine%20still%20life&width=1000&height=1200&seq=baoliao-about-01&orientation=portrait"
              alt="18爆料网编辑整理内容的工作场景"
              title="18爆料网 - 编辑流程"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section id="principles" className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="PRINCIPLES"
            title="我们的内容原则"
            desc="热闹归热闹，底线要有。以下四条是编辑组每天都会对照的准则。"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                  <i className={p.icon}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          eyebrow="VOICES"
          title="读者怎么说"
          desc="长期读下去的人，才是我们最看重的参考。"
        />
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure
              key={t.name}
              className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <div className="flex gap-1 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <blockquote className="mt-4 flex-1 text-sm leading-7 text-foreground-700">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 border-t border-background-200 pt-4 text-sm font-medium text-foreground-950">
                {t.name}
                <span className="ml-2 text-xs font-normal text-foreground-500">{t.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section id="contact" className="border-t border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading
                eyebrow="CONTACT"
                title="联系我们"
                desc="有线索、有更正、想合作，都可以找到我们。"
              />
              <ul className="mt-8 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                    <i className="ri-mail-send-line"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-950">线索投稿与合作</h3>
                    <a
                      href={`mailto:${siteInfo.email}`}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      {siteInfo.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-950">编辑时间</h3>
                    <p className="text-sm text-foreground-600">每日 09:00 - 22:00，热榜每两小时更新</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                    <i className="ri-shield-user-line"></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-950">隐私保护</h3>
                    <p className="text-sm text-foreground-600">
                      投稿人的信息严格保密，涉及个人隐私的内容不予发布
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                订阅吃瓜日报
              </h3>
              <SubscribeForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}